import {
  UserAuthenticationModal,
  UserAuthResult,
  AuthStep,
  AuthenticationProvider,
} from '@sparemin/auth';
import { ThemeProvider } from '@sparemin/blockhead';
import { userProfileQueryKeys } from 'api/hooks/queries';
import { getUserProfile } from 'api/user';
import { notifyError } from 'components/notification';
import { useAuth } from 'context/AuthContext';
import { useEventTracking } from 'context/EventTrackingContext';
import { useConnectedModal, useModalControls } from 'context/ModalContext';
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { GENERIC_ERROR_MESSAGE } from 'utils/constants';
import useSetAuthModalWithParams from './useSetAuthModalWithParams';

export interface AuthModalProps {}

const MODAL_FEATURES: AuthStep[] = [
  'login',
  'sign-up',
  'forgot-password',
  'reset-password',
];

const AuthModal: React.FC<AuthModalProps> = () => {
  const modalName = 'Auth';
  const { login, isAuthenticated, onAuthSuccess } = useAuth();
  const { trackUserSignup, trackUserLogin, setUserTrackingInfo } =
    useEventTracking();

  const { show, handleRequestClose, handleAfterClose } = useConnectedModal({
    name: modalName,
  });

  const { open } = useModalControls();

  const queryClient = useQueryClient();
  const { modalShouldOpen, clearParams } = useSetAuthModalWithParams();

  // if query params are set to modal=authentication, open the auth modal
  // then clear the query string
  useEffect(() => {
    if (modalShouldOpen && !isAuthenticated) {
      open({ name: modalName });
      clearParams();
    } else if (modalShouldOpen && isAuthenticated) clearParams();
  }, [isAuthenticated, modalShouldOpen, open, clearParams, modalName]);

  const handleSuccess = async (
    step: AuthStep,
    provider?: AuthenticationProvider | undefined,
    data?: UserAuthResult,
  ) => {
    try {
      if (data?.spareminToken && data.userId) {
        const profile = await getUserProfile({
          userId: data.userId,
          token: data.spareminToken,
        });
        if (profile && profile.email) {
          queryClient.setQueryData(userProfileQueryKeys.all, profile);
          if (step === 'sign-up') {
            trackUserSignup(profile.email);
          }
          if (step === 'login') {
            trackUserLogin();
          }
          setUserTrackingInfo(profile.email, data.userId);
          login(data);
          handleRequestClose();
          onAuthSuccess?.();
        }
      }
    } catch (error) {
      notifyError({ heading: GENERIC_ERROR_MESSAGE });
    }
  };

  return (
    <ThemeProvider theme="light">
      <UserAuthenticationModal
        initialStep="sign-up"
        features={MODAL_FEATURES}
        onSuccess={handleSuccess}
        isOpen={show}
        onClose={handleRequestClose}
        onAfterClose={handleAfterClose}
      />
    </ThemeProvider>
  );
};
export default AuthModal;
